import React, { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  getFinanceAspsps,
  getFinanceStatus,
  patchFinanceAccount,
  startFinanceAuth,
  updateSettings,
} from "../../utils/apiClient";
import { MONTH_CALENDAR, MONTH_LAST_BANK_DAY } from "../../utils/financeCalendar";
import { forceSyncFinance } from "../../utils/financeSync";

function formatWhen(value) {
  if (!value) return "–";
  try {
    return new Date(value).toLocaleString("da-DK");
  } catch {
    return String(value);
  }
}

function BankAccountsPanel() {
  const [status, setStatus] = useState(null);
  const [aspsps, setAspsps] = useState([]);
  const [aspspName, setAspspName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [monthMode, setMonthMode] = useState(MONTH_LAST_BANK_DAY);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getFinanceStatus();
      setStatus(data);
      setAspspName(data.default_aspsp || "");
      if (data.finance_month_mode) setMonthMode(data.finance_month_mode);
      setAliasDrafts(
        Object.fromEntries(
          (data.accounts || []).map((account) => [account.account_uuid, account.alias || ""])
        )
      );
      if (data.configured) {
        try {
          const list = await getFinanceAspsps(data.default_country || "DK");
          setAspsps(Array.isArray(list) ? list : []);
        } catch {
          setAspsps([]);
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const data = await startFinanceAuth({
        aspsp_name: aspspName || status?.default_aspsp,
        aspsp_country: status?.default_country || "DK",
        origin: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setError("Ingen omdirigering fra banken");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke starte banklogin");
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const result = await forceSyncFinance();
      setInfo(`Synkroniseret ${result.accounts || 0} konti (${result.upserted || 0} nye posteringer).`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Synkronisering mislykkedes");
    } finally {
      setBusy(false);
    }
  };

  const toggleMonitored = async (account, monitored) => {
    setError("");
    try {
      await patchFinanceAccount(account.account_uuid, { monitored });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke opdatere konto");
    }
  };

  const setDefaultAccount = async (account) => {
    setError("");
    try {
      await patchFinanceAccount(account.account_uuid, { is_default: true });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke sætte standardkonto");
    }
  };

  const saveMonthMode = async (next) => {
    setMonthMode(next);
    setError("");
    setInfo("");
    try {
      const data = await updateSettings({ finance_month_mode: next });
      setMonthMode(data.finance_month_mode || next);
      setInfo("Økonomimåned gemt");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme økonomimåned");
    }
  };

  const saveAlias = async (account) => {
    const next = (aliasDrafts[account.account_uuid] || "").trim();
    if (next === (account.alias || "")) return;
    setError("");
    try {
      await patchFinanceAccount(account.account_uuid, { alias: next });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme kaldenavn");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const accounts = status?.accounts || [];
  const expiring = (status?.sessions || []).some((session) => {
    if (!session.valid_until || session.status !== "active") return false;
    const left = new Date(session.valid_until).getTime() - Date.now();
    return left < 14 * 24 * 60 * 60 * 1000;
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Bank
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Forbind Lån & Spar (eller en anden dansk bank) og vælg hvilke konti der skal
        overvåges. Lokalt sendes du tilbage til https://localhost:3000 (accepter
        det selvunderskrevne certifikat første gang).
      </Typography>

      {!status?.configured ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Enable Banking er ikke konfigureret. Tilføj ENABLEBANKING_APP_ID og
          ENABLEBANKING_PRIVATE_KEY_FILE i secrets.toml.
        </Alert>
      ) : null}
      {status?.configured && status.application_active === false ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Enable Banking-appen er registreret, men ikke aktiveret. I Control Panel:
          vælg Activate by linking accounts og tilknyt dine egne konti. Derefter kan
          du hente banker og logge ind.
        </Alert>
      ) : null}
      {expiring ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bankgodkendelsen udløber snart eller er udløbet. Forbind kontoen igen.
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {info}
        </Alert>
      ) : null}

      <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1, maxWidth: 560 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Økonomimåned</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          En bankmåned går fra sidste bankdag i forrige måned til dagen før næste løn-bankdag.
          Kalendermåned er den 1. til den sidste dato.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Måned starter</InputLabel>
          <Select
            label="Måned starter"
            value={monthMode}
            onChange={(event) => saveMonthMode(event.target.value)}
          >
            <MenuItem value={MONTH_LAST_BANK_DAY}>Sidste bankdag (lønperiode)</MenuItem>
            <MenuItem value={MONTH_CALENDAR}>Kalender (1. til sidste)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Bank</InputLabel>
          <Select
            label="Bank"
            value={aspspName}
            onChange={(event) => setAspspName(event.target.value)}
            disabled={!status?.configured || busy}
          >
            {aspspName && !aspsps.some((item) => item.name === aspspName) ? (
              <MenuItem value={aspspName}>{aspspName}</MenuItem>
            ) : null}
            {aspsps.map((item) => (
              <MenuItem key={`${item.country}-${item.name}`} value={item.name}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={connect} disabled={!status?.configured || busy}>
          {busy ? "Vent…" : "Forbind bank"}
        </Button>
        <Button variant="outlined" onClick={syncNow} disabled={!status?.configured || busy || !accounts.length}>
          Synkroniser nu
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Sidst synkroniseret: {formatWhen(status?.last_synced_at)}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Kaldenavn</TableCell>
            <TableCell>Navn</TableCell>
            <TableCell>Konto nr.</TableCell>
            <TableCell>Saldo</TableCell>
            <TableCell>Standard</TableCell>
            <TableCell>Overvåg</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary">
                  Ingen konti endnu. Forbind banken for at hente konti.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            accounts.map((account) => (
              <TableRow key={account.account_uuid}>
                <TableCell sx={{ minWidth: 180 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="fx Lønkonto"
                    value={aliasDrafts[account.account_uuid] ?? account.alias ?? ""}
                    onChange={(event) =>
                      setAliasDrafts((prev) => ({
                        ...prev,
                        [account.account_uuid]: event.target.value,
                      }))
                    }
                    onBlur={() => saveAlias(account)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.target.blur();
                    }}
                    inputProps={{ "aria-label": "Kaldenavn" }}
                  />
                </TableCell>
                <TableCell>
                  {account.name || account.details || "Konto"}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {account.aspsp_name} · {account.currency}
                  </Typography>
                </TableCell>
                <TableCell>{account.account_number || "–"}</TableCell>
                <TableCell>
                  {account.current_balance != null
                    ? Number(account.current_balance).toLocaleString("da-DK", {
                        style: "currency",
                        currency: account.currency || "DKK",
                      })
                    : "–"}
                  {Number(account.pending_balance || 0) !== 0 ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      inkl. afventende {Number(account.pending_balance).toLocaleString("da-DK", {
                        style: "currency",
                        currency: account.currency || "DKK",
                      })}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Radio
                    checked={!!account.is_default}
                    onChange={() => setDefaultAccount(account)}
                    inputProps={{ "aria-label": "Standardkonto" }}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={!!account.monitored}
                    onChange={(event) => toggleMonitored(account, event.target.checked)}
                    inputProps={{ "aria-label": "Overvåg konto" }}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

export default BankAccountsPanel;
